import { supabase } from "@/integrations/supabase/client";
import { avaliarAcesso, type Acesso, type StatusAssinatura } from "@domain/billing";
import type { AoEstourarCota } from "@domain/cota";

export interface EstadoAssinatura {
  status: StatusAssinatura;
  assentos: number;
  membrosAtivos: number;
  periodoFim: Date | null;
  trialFim: Date | null;
  cancelarNoFim: boolean;
  temClienteAsaas: boolean;
  /** CPF/CNPJ ja informado. Sem ele o Asaas nao cobra. */
  temDocumento: boolean;
  planoNome: string | null;
  precoCentavos: number | null;
  moeda: string;
  /** Falso quando o plano e pacote: o preco vale pela empresa, nao por pessoa. */
  porAssento: boolean;
  /** Teto de membros do pacote. Nulo e sem teto. */
  maxAssentos: number | null;
  conversasIaInclusas: number | null;
  /** Conversas distintas que o agente ja atendeu no ciclo. */
  iaConsumidas: number;
  iaSaldoComprado: number;
  iaAoEstourar: AoEstourarCota;
  /** Valor do assento alem dos inclusos. Nulo = o plano nao vende assento extra. */
  assentoCentavos: number | null;
  pacoteIaTamanho: number | null;
  pacoteIaCentavos: number | null;
  suspensa: boolean;
  suspensaMotivo: string | null;
  acesso: Acesso;
}

function data(valor: string | null): Date | null {
  return valor ? new Date(valor) : null;
}

export async function estadoAssinatura(): Promise<EstadoAssinatura | null> {
  const { data: linhas, error } = await supabase.rpc("estado_assinatura");
  if (error) throw error;

  const l = (linhas ?? [])[0];
  if (!l) return null;

  const status = l.status as StatusAssinatura;
  const periodoFim = data(l.periodo_fim);
  const trialFim = data(l.trial_fim);

  return {
    status,
    assentos: l.assentos,
    membrosAtivos: l.membros_ativos,
    periodoFim,
    trialFim,
    cancelarNoFim: l.cancelar_no_fim,
    temClienteAsaas: l.tem_cliente_asaas,
    temDocumento: l.tem_documento,
    planoNome: l.plano_nome,
    precoCentavos: l.preco_centavos,
    moeda: l.moeda ?? "brl",
    porAssento: l.por_assento ?? true,
    maxAssentos: l.max_assentos ?? null,
    conversasIaInclusas: l.conversas_ia_inclusas ?? null,
    iaConsumidas: l.ia_consumidas ?? 0,
    iaSaldoComprado: l.ia_saldo_comprado ?? 0,
    iaAoEstourar: (l.ia_ao_estourar ?? "parar") as AoEstourarCota,
    assentoCentavos: l.assento_centavos ?? null,
    pacoteIaTamanho: l.pacote_ia_tamanho ?? null,
    pacoteIaCentavos: l.pacote_ia_centavos ?? null,
    suspensa: Boolean(l.suspensa),
    suspensaMotivo: l.suspensa_motivo,
    // A mesma regra do servidor, calculada aqui para a tela nao inventar a sua.
    acesso: avaliarAcesso({
      assinatura: { status, periodoFim, trialFim, cancelarNoFim: l.cancelar_no_fim },
      agora: new Date(),
    }),
  };
}

type Acao = "checkout" | "fatura" | "pacote_ia" | "cancelar";

async function chamar(acao: Acao, extra?: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data: resposta, error } = await supabase.functions.invoke("billing", {
    body: { acao, ...extra },
  });
  if (error) {
    // A function responde a frase para a pessoa no corpo do erro (CPF
    // invalido, equipe maior que o plano). Sem ler o corpo, sobra "non-2xx".
    const corpo = await (error as { context?: Response }).context?.json?.().catch(() => null);
    throw new Error(corpo?.erro ?? error.message);
  }
  return resposta ?? {};
}

/** Devolve a URL da fatura hospedada pelo Asaas. O cartão nunca passa por aqui. */
async function fatura(acao: Acao, extra?: Record<string, unknown>): Promise<string> {
  const resposta = await chamar(acao, extra);
  if (!resposta.url) throw new Error((resposta.erro as string) ?? "O Asaas não devolveu a fatura");
  return resposta.url as string;
}

/**
 * O que fazer quando a cota de IA acaba.
 *
 * Nao ha padrao certo: parar protege a fatura, cobrar protege o atendimento.
 * Quem decide e quem paga, e por isso isto e uma escolha e nao uma regra nossa.
 */
export async function definirIaAoEstourar(valor: AoEstourarCota): Promise<void> {
  const { error } = await supabase.rpc("definir_ia_ao_estourar", { p_valor: valor });
  if (error) throw error;
}

/** O documento so vai na primeira vez; depois o servidor ja tem. */
export const abrirCheckout = (documento?: string) => fatura("checkout", { documento });
/** Compra avulsa de mais conversas de IA. Nao mexe na assinatura. */
export const abrirCompraDePacote = () => fatura("pacote_ia");
/** Fatura em aberto, ou o recibo da ultima paga. */
export const abrirFatura = () => fatura("fatura");

/** Cancela no Asaas. O ciclo ja pago continua valendo ate o fim. */
export async function cancelarAssinatura(): Promise<void> {
  await chamar("cancelar");
}
